import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import defaultSlotsConfig from "../../../components/SlotsConfig_Summer2026";
import { supabase } from "../../../supabaseClient";
import Pathway from "../../../components/Pathway";
import "../../../components/Pathway.css";
import {
  TENT_SUBDIVISIONS,
  TENT_COLORS,
  TENT_LABELS,
  FACING_LABEL,
  getFacingBorders,
  getOrientedLayoutStyle,
  FacingArrow,
  CrownIcon,
  PencilIcon,
  TileSubPreview,
} from "../../../components/Tentvisuals";
import "./Step4.css";

const ZOOM_LEVELS = [0.5, 0.65, 0.8, 1, 1.25, 1.5];
const DEFAULT_ZOOM = 2;

// Broad type matching to handle hyphen vs underscore config variations
const ALLOWED_CONFIG_TYPES = {
  "20x20_single": ["20x20", "20x20_single", "20x20-single"],
  "20x20_shared": ["20x20", "20x20_shared", "20x20-shared", "20x20-double", "20x20_double"],
  "10x10_single": ["10x10", "10x10-single", "10x10_single"],
  pod_shared: ["17x17-pod", "17x17_pod", "pod_shared"],
  food_court: ["Food Court", "food_court"],
  "20x20_open": ["20x20_open", "20x20-open"],
  biz_boost: ["biz_boost"],
  sponsored: ["sponsored"],
  WFTO: ["WFTO"],
  ITC: ["ITC"],
};

const INELIGIBLE_TENT_TYPES = ["food_court", "biz_boost", "sponsored", "20x20_open", "WFTO", "ITC"];

const INELIGIBLE_LABELS = {
  food_court: "Food Court",
  biz_boost: "Biz Boost Program",
  sponsored: "Sponsored",
  WFTO: "WFTO",
  ITC: "ITC",
};

const REDIRECT_OPTIONS = [
  {
    value: "20x20_single",
    label: "20×20 Individual",
    price: "KES 90,000",
    description: "Your own private 20×20 space. Full control of your layout and branding.",
    icon: "🏠",
  },
  {
    value: "20x20_shared",
    label: "20×20 Shared",
    price: "KES 55,000",
    description: "Share a 20×20 space with one other vendor. Great value for smaller setups.",
    icon: "🤝",
  },
];

export default function Step4ChooseSpot({ vendor, event, app: initialApp, formData, setFormData, onNext }) {
  const eventKey = event?.event_key || event?.id;

  const [application, setApplication] = useState(initialApp || null);
  const [activeTentType, setActiveTentType] = useState(initialApp?.tent_type || "20x20_shared");

  // Dynamic published layout state with static fallback
  const [slotsConfig, setSlotsConfig] = useState(defaultSlotsConfig);
  const [layoutLoading, setLayoutLoading] = useState(true);

  const [subBookings, setSubBookings] = useState({});
  const [reservedSlots, setReservedSlots] = useState([]);

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);

  const [choosingSpot, setChoosingSpot] = useState(false);
  const [showOptIn, setShowOptIn] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectChoice, setRedirectChoice] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM);
  const [hasScrolledToEntry, setHasScrolledToEntry] = useState(false);

  const gridRef = useRef(null);
  const slotRefs = useRef({});

  const showMsg = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  useEffect(() => {
    if (!initialApp) return;
    setApplication(initialApp);
    if (initialApp.tent_type) setActiveTentType(initialApp.tent_type);

    if (initialApp.chosen_spot) {
      setFormData((prev) => ({ ...prev, spot: initialApp.chosen_spot }));
    }

    if (initialApp.tent_type === "20x20_open") {
      setShowRedirect(true);
      return;
    }

    if (INELIGIBLE_TENT_TYPES.includes(initialApp.tent_type)) {
      return;
    }

    setShowOptIn(true);
  }, [initialApp?.id, initialApp?.tent_type, setFormData]);

  // 1. Fetch published layout from layouts_published
  const fetchPublishedLayout = useCallback(async () => {
    if (!eventKey) return;
    try {
      const { data, error } = await supabase
        .from("layouts_published")
        .select("items")
        .eq("event_key", eventKey)
        .maybeSingle();

      if (error) {
        console.warn("Error reading layouts_published, falling back to static config:", error.message);
        setSlotsConfig(defaultSlotsConfig);
        return;
      }

      if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
        setSlotsConfig(data.items);
      } else {
        setSlotsConfig(defaultSlotsConfig);
      }
    } catch (err) {
      console.error("Layout fetch failed, using fallback:", err);
      setSlotsConfig(defaultSlotsConfig);
    } finally {
      setLayoutLoading(false);
    }
  }, [eventKey]);

  // 2. Fetch active slot bookings
  const fetchSlots = useCallback(async () => {
    if (!eventKey) return;

    const { data, error } = await supabase
      .from("vendor_slots")
      .select("id, slot_id, sub_id, vendor_application_id, tent_mode, reserved, self_selected")
      .eq("event_key", eventKey);

    if (error) {
      console.error("Slots fetch error:", error);
      return;
    }

    if (!data?.length) {
      setSubBookings({});
      setReservedSlots([]);
      return;
    }

    const appIds = [...new Set(data.filter((r) => r.vendor_application_id).map((r) => r.vendor_application_id))];
    let appNames = {};
    if (appIds.length > 0) {
      const { data: apps, error: appErr } = await supabase
        .from("vendor_applications")
        .select("id, vendors(brand_name)")
        .in("id", appIds);
      if (appErr) console.error("vendor_applications fetch error:", appErr);
      apps?.forEach((a) => {
        appNames[a.id] = a.vendors?.brand_name || "Unknown";
      });
    }

    const map = {};
    const reserved = [];

    data.forEach((row) => {
      if (row.reserved) {
        reserved.push(row.slot_id);
        return;
      }
      const sub = row.sub_id || "A";
      const key = `${row.slot_id}:${sub}`;
      if (row.vendor_application_id) {
        map[key] = {
          id: row.id,
          vendorId: row.vendor_application_id,
          vendorName: appNames[row.vendor_application_id] || "Unknown",
          tent_mode: row.tent_mode,
          selfSelected: row.self_selected,
        };
      }
    });

    setSubBookings(map);
    setReservedSlots(reserved);
  }, [eventKey]);

  // Initial load
  useEffect(() => {
    if (eventKey) {
      fetchPublishedLayout();
      fetchSlots();
    }
  }, [eventKey, fetchPublishedLayout, fetchSlots]);

  // 3. Realtime Subscription on both layouts_published and vendor_slots
  useEffect(() => {
    if (!eventKey) return;

    // Layout changes channel
    const layoutChannel = supabase
      .channel(`realtime_layout_${eventKey}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "layouts_published",
          filter: `event_key=eq.${eventKey}`,
        },
        (payload) => {
          if (payload.new?.items && Array.isArray(payload.new.items)) {
            setSlotsConfig(payload.new.items);
          }
        }
      )
      .subscribe();

    // Slot bookings channel
    const slotsChannel = supabase
      .channel(`realtime_vendor_slots_${eventKey}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "vendor_slots",
          filter: `event_key=eq.${eventKey}`,
        },
        () => {
          fetchSlots();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(layoutChannel);
      supabase.removeChannel(slotsChannel);
    };
  }, [eventKey, fetchSlots]);

  // Scroll to Entry on first open
  useEffect(() => {
    if (!choosingSpot || hasScrolledToEntry) return;
    const t = setTimeout(() => {
      const el = slotRefs.current["Entry"];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        setHasScrolledToEntry(true);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [choosingSpot, hasScrolledToEntry]);

  // Zoom handling
  const zoomIn = useCallback(() => setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1)), []);
  const zoomOut = useCallback(() => setZoomIndex((i) => Math.max(i - 1, 0)), []);
  const zoomReset = useCallback(() => setZoomIndex(DEFAULT_ZOOM), []);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.deltaY < 0 ? zoomIn() : zoomOut();
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [zoomIn, zoomOut]);

  const handleRedirectConfirm = async () => {
    if (!redirectChoice || !application) return;
    setSwitching(true);
    showMsg("", "info");
    try {
      const { error } = await supabase
        .from("vendor_applications")
        .update({ tent_type: redirectChoice, chosen_spot: null })
        .eq("id", application.id);
      if (error) throw error;

      setActiveTentType(redirectChoice);
      setFormData((prev) => ({ ...prev, spot: null }));
      setShowRedirect(false);
      setShowOptIn(true);
      await fetchSlots();
    } catch (err) {
      showMsg("Failed to update space type: " + err.message, "error");
    } finally {
      setSwitching(false);
    }
  };

  const appTentType = activeTentType;
  const isIneligible = INELIGIBLE_TENT_TYPES.includes(appTentType);
  const allowedTypes = useMemo(() => {
    return ALLOWED_CONFIG_TYPES[appTentType] || ["20x20", "20x20_shared", "20x20-shared"];
  }, [appTentType]);

  const is20x20Vendor = appTentType === "20x20_single" || appTentType === "20x20_shared";
  const wantsSharing = appTentType === "20x20_shared";

  const getEffectiveType = useCallback(
    (slot) => {
      if (reservedSlots.includes(slot.id)) return "reserved";
      const normalizedSlotType = (slot.type || "").replace(/_/g, "-");

      if (normalizedSlotType.startsWith("20x20")) {
        const existing = Object.entries(subBookings).find(([k]) => k.startsWith(slot.id + ":"));
        if (wantsSharing) return "20x20-double";
        if (existing) {
          if (!existing[1].tent_mode) {
            const subsPresent = new Set(
              Object.keys(subBookings)
                .filter((k) => k.startsWith(slot.id + ":"))
                .map((k) => k.split(":")[1])
            );
            return subsPresent.size > 1 || subsPresent.has("B") ? "20x20-double" : "20x20-single";
          }
          return existing[1].tent_mode;
        }
        if (is20x20Vendor) return wantsSharing ? "20x20-double" : "20x20-single";
        return "20x20";
      }
      return slot.type;
    },
    [subBookings, reservedSlots, is20x20Vendor, wantsSharing]
  );

  const getTileSubBookings = useCallback(
    (slotId) => {
      const out = {};
      Object.entries(subBookings).forEach(([k, v]) => {
        if (k.startsWith(slotId + ":")) out[k.split(":")[1]] = v;
      });
      return out;
    },
    [subBookings]
  );

  const isSlotTypeAllowed = useCallback(
    (slot) => {
      const normalized = (slot.type || "").replace(/_/g, "-");
      return allowedTypes.some((t) => t.replace(/_/g, "-") === normalized);
    },
    [allowedTypes]
  );

  const isSlotEligible = useCallback(
    (slot) => {
      if (slot.type === "blocker" || slot.type === "pathway" || slot.type === "space") return false;
      if (reservedSlots.includes(slot.id)) return false;
      if (!isSlotTypeAllowed(slot)) return false;

      if (is20x20Vendor) {
        const tileSubs = getTileSubBookings(slot.id);
        const bookedSubs = Object.keys(tileSubs);

        const existingMode = Object.values(tileSubs)[0]?.tent_mode;
        if (existingMode === "20x20-single") return false;

        if (wantsSharing) {
          return bookedSubs.length < 2;
        } else {
          return bookedSubs.length === 0;
        }
      }
      return true;
    },
    [reservedSlots, isSlotTypeAllowed, is20x20Vendor, wantsSharing, getTileSubBookings]
  );

  const getTileCap = useCallback(
    (slot) => {
      const et = getEffectiveType(slot);
      return TENT_SUBDIVISIONS[et]?.subs.length ?? 1;
    },
    [getEffectiveType]
  );

  const mySubKey = Object.entries(subBookings).find(([, v]) => v.vendorId === application?.id)?.[0] || null;
  const mySlotId = mySubKey ? mySubKey.split(":")[0] : null;

  const claimSub = async (slotId, sub, tentModeForClaim) => {
    if (!application || !eventKey) return;
    if (mySlotId && mySlotId !== slotId) {
      showMsg("You already have a spot claimed. Release it first to choose another.", "error");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("vendor_slots").insert({
      event_key: eventKey,
      slot_id: slotId,
      sub_id: sub,
      vendor_application_id: application.id,
      tent_mode: tentModeForClaim,
      self_selected: true,
    });
    if (error) {
      showMsg("Failed to claim slot: " + error.message, "error");
    } else {
      const slotConfig = slotsConfig.find((s) => s.id === slotId);
      setFormData((prev) => ({ ...prev, spot: slotId }));
      showMsg(
        `${slotConfig?.label || slotId}${
          sub !== "A" || TENT_SUBDIVISIONS[tentModeForClaim]?.subs.length > 1 ? ` (${sub})` : ""
        } claimed!`,
        "success"
      );
      await fetchSlots();
      setSelectedSub(null);
      setSelectedSlot(null);
    }
    setLoading(false);
  };

  const releaseSlot = async () => {
    if (!application || !eventKey) return;
    setLoading(true);
    const { error } = await supabase
      .from("vendor_slots")
      .delete()
      .eq("event_key", eventKey)
      .eq("vendor_application_id", application.id);
    if (error) {
      showMsg("Failed to release slot: " + error.message, "error");
    } else {
      setFormData((prev) => ({ ...prev, spot: null }));
      showMsg("Slot released.", "info");
      await fetchSlots();
      setSelectedSlot(null);
      setSelectedSub(null);
    }
    setLoading(false);
  };

  const activeSlotConfig = selectedSlot ? slotsConfig.find((s) => s.id === selectedSlot) : null;
  const activeEffType = activeSlotConfig ? getEffectiveType(activeSlotConfig) : null;
  const activeTileSubs = selectedSlot ? getTileSubBookings(selectedSlot) : {};
  const activeDef = activeEffType ? TENT_SUBDIVISIONS[activeEffType] : null;
  const activeIsMultiSub = activeDef && activeDef.subs.length > 1;
  const activeIsReserved = selectedSlot ? reservedSlots.includes(selectedSlot) : false;
  const activeEligible = activeSlotConfig ? isSlotEligible(activeSlotConfig) : false;
  const activeIAmHere = mySlotId === selectedSlot;
  const activeFilledCount = activeDef ? activeDef.subs.filter((s) => activeTileSubs[s]).length : 0;
  const activeCapacity = activeDef?.subs.length ?? 1;
  const activeOrientedStyle = activeDef
    ? getOrientedLayoutStyle(activeDef.layout, activeSlotConfig?.facing)
    : undefined;

  const claimTentMode = is20x20Vendor ? (wantsSharing ? "20x20-double" : "20x20-single") : activeEffType;

  const renderBlocker = (slot) => {
    const isEntry = slot.id === "Entry";
    const isExit = slot.id === "Exit";
    return (
      <div
        key={slot.id}
        ref={(el) => (slotRefs.current[slot.id] = el)}
        className={[
          "s4-slot s4-slot--blocker",
          isEntry ? "s4-slot--entry" : "",
          isExit ? "s4-slot--exit" : "",
          !isEntry && !isExit ? "s4-slot--gap" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ gridColumn: slot.gridColumn, gridRow: slot.gridRow }}
      >
        {isEntry && (
          <>
            <span className="s4-blocker-icon">→</span>
            <span>Entry</span>
          </>
        )}
        {isExit && (
          <>
            <span className="s4-blocker-icon">←</span>
            <span>Exit</span>
          </>
        )}
        {!isEntry && !isExit && <span className="s4-blocker-icon">↔</span>}
      </div>
    );
  };

  const renderPathway = (slot) => <Pathway key={slot.id} slot={slot} />;

  if (isIneligible) {
    return (
      <div className="s4-container">
        <h2 className="s4-title">Choose Your Spot</h2>
        <div className="s4-ineligible-card">
          <div className="s4-ineligible-icon">🏷️</div>
          <h3 className="s4-ineligible-heading">Spot Selection Not Available</h3>
          <p className="s4-ineligible-body">
            Due to the nature of your <strong>{INELIGIBLE_LABELS[appTentType] || appTentType}</strong> space,
            self-allocation is not available. The event organiser will assign your location and reach out to you directly.
          </p>
          <p className="s4-ineligible-note">No action is required from you on this step.</p>
        </div>
        {onNext && (
          <div className="s4-ineligible-actions">
            <button className="s4-btn s4-btn--primary" onClick={onNext}>
              Continue →
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="s4-container">
      <h2 className="s4-title">Choose Your Spot</h2>
      <p className="s4-subtitle">Select your preferred stall location from the layout below.</p>

      {/* Redirect Modal */}
      {showRedirect && (
        <div className="s4-backdrop">
          <div className="s4-modal s4-modal--redirect">
            <div className="s4-redirect-alert">
              <span className="s4-redirect-alert-icon">⚠️</span>
              <div>
                <p className="s4-redirect-alert-title">Your space type needs to change</p>
                <p className="s4-redirect-alert-body">
                  The <strong>20×20 Open Space</strong> is reserved for organiser-selected vendors only and is now fully
                  allocated. Select a space type below to continue your onboarding.
                </p>
              </div>
            </div>
            <p className="s4-redirect-prompt">Select your new space type to continue:</p>
            <div className="s4-redirect-options">
              {REDIRECT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={[
                    "s4-redirect-card",
                    redirectChoice === opt.value ? "s4-redirect-card--selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setRedirectChoice(opt.value)}
                  disabled={switching}
                >
                  <div className="s4-redirect-card-top">
                    <span className="s4-redirect-card-icon">{opt.icon}</span>
                    <div className="s4-redirect-card-info">
                      <span className="s4-redirect-card-label">{opt.label}</span>
                      <span className="s4-redirect-card-price">
                        {opt.price} <small>+ 16% VAT</small>
                      </span>
                    </div>
                    <span className="s4-redirect-card-check">{redirectChoice === opt.value ? "✓" : ""}</span>
                  </div>
                  <p className="s4-redirect-card-desc">{opt.description}</p>
                </button>
              ))}
            </div>
            {message && <div className={`s4-message s4-message--${messageType}`} style={{ marginTop: 12 }}>{message}</div>}
            <button
              className="s4-btn s4-btn--primary"
              style={{ width: "100%", marginTop: 20 }}
              onClick={handleRedirectConfirm}
              disabled={!redirectChoice || switching}
            >
              {switching
                ? "Updating your space…"
                : redirectChoice
                ? `Confirm ${REDIRECT_OPTIONS.find((o) => o.value === redirectChoice)?.label} →`
                : "Select a space type to continue"}
            </button>
          </div>
        </div>
      )}

      {/* Opt-In Modal */}
      {showOptIn && !showRedirect && (
        <div className="s4-backdrop">
          <div className="s4-modal">
            <h3 className="s4-modal-title">Choose Your Spot</h3>
            {application?.chosen_spot ? (
              <p className="s4-modal-desc">
                You currently have{" "}
                <strong>
                  {slotsConfig.find((s) => s.id === application.chosen_spot)?.label || application.chosen_spot}
                </strong>
                . Would you like to view your spot on the map, or change it?
              </p>
            ) : (
              <p className="s4-modal-desc">
                You're now on{" "}
                <strong>
                  {activeTentType === "20x20_single"
                    ? "20×20 Individual"
                    : activeTentType === "20x20_shared"
                    ? "20×20 Shared"
                    : TENT_LABELS[allowedTypes[0]] || "your space"}
                </strong>
                . Would you like to pick your exact location from the event layout?
              </p>
            )}
            <div className="s4-opt-list">
              <div className="s4-opt-item">
                <span className="s4-opt-check">✓</span>Self-selection fee: <strong>KES 7,000 + VAT</strong>
              </div>
              <div className="s4-opt-item">
                <span className="s4-opt-check">✓</span>Limited spots — first come, first served
              </div>
              <div className="s4-opt-item">
                <span className="s4-opt-check">✓</span>Skip this and the organiser will allocate your spot at no extra charge
              </div>
            </div>
            <div className="s4-modal-actions">
              <button
                className="s4-btn s4-btn--primary"
                onClick={() => {
                  setChoosingSpot(true);
                  setShowOptIn(false);
                }}
              >
                {application?.chosen_spot ? "View / Change My Spot" : "Yes, Choose My Spot"}
              </button>
              <button
                className="s4-btn s4-btn--ghost"
                onClick={() => {
                  if (!application?.chosen_spot) {
                    setFormData((prev) => ({ ...prev, spot: null }));
                  }
                  setShowOptIn(false);
                }}
              >
                {application?.chosen_spot ? "Keep My Current Spot" : "Skip — Organiser Allocates"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claimed Spot Status */}
      {choosingSpot && mySlotId && (
        <div className="s4-current-spot">
          <span>
            📍 Your spot: <strong>{slotsConfig.find((s) => s.id === mySlotId)?.label || mySlotId}</strong>
          </span>
          <button className="s4-release-btn" onClick={releaseSlot} disabled={loading}>
            Release Spot
          </button>
        </div>
      )}

      {message && choosingSpot && <div className={`s4-message s4-message--${messageType}`}>{message}</div>}

      {/* Legend */}
      {choosingSpot && (
        <div className="s4-legend">
          {allowedTypes.map((type) => (
            <div key={type} className="s4-legend-item">
              <span className="s4-legend-dot" style={{ background: TENT_COLORS[type] || "#999" }} />
              <span>{TENT_LABELS[type] || type} — <em>your slots</em></span>
            </div>
          ))}
          <div className="s4-legend-item s4-legend-item--dim">
            <span className="s4-legend-dot" style={{ background: TENT_COLORS.reserved }} />
            <span>Reserved</span>
          </div>
          <div className="s4-legend-item s4-legend-item--dim">
            <span className="s4-legend-dot s4-legend-dot--entry" />
            <span>Entry</span>
          </div>
          <div className="s4-legend-item s4-legend-item--dim">
            <span className="s4-legend-dot s4-legend-dot--exit" />
            <span>Exit</span>
          </div>
        </div>
      )}

      {/* Map Canvas */}
      {choosingSpot && (
        <div className="s4-grid-canvas" ref={gridRef}>
          <div className="s4-zoom-controls">
            <button className="s4-zoom-btn" onClick={zoomOut} disabled={zoomIndex === 0} aria-label="Zoom out">−</button>
            <button className="s4-zoom-btn s4-zoom-btn--label" onClick={zoomReset}>{Math.round(ZOOM_LEVELS[zoomIndex] * 100)}%</button>
            <button className="s4-zoom-btn" onClick={zoomIn} disabled={zoomIndex === ZOOM_LEVELS.length - 1} aria-label="Zoom in">+</button>
          </div>
          <div className="s4-zoom-hint">Ctrl + scroll to zoom</div>

          <div className="s4-grid-scroll">
            <div className="s4-grid-transform" style={{ transform: `scale(${ZOOM_LEVELS[zoomIndex]})` }}>
              <div className="s4-grid">
                {slotsConfig.map((slot) => {
                  if (slot.type === "pathway") return renderPathway(slot);
                  if (slot.type === "blocker") return renderBlocker(slot);
                  if (slot.id === "P0") {
                    return (
                      <div
                        key="P0"
                        ref={(el) => (slotRefs.current["P0"] = el)}
                        className="s4-slot s4-slot--podium"
                        style={{ gridColumn: slot.gridColumn, gridRow: slot.gridRow }}
                      >
                        PODIUM
                      </div>
                    );
                  }

                  const et = getEffectiveType(slot);
                  const tileSubs = getTileSubBookings(slot.id);
                  const used = Object.keys(tileSubs).length;
                  const cap = getTileCap(slot);
                  const isRes = reservedSlots.includes(slot.id);
                  const iAmHere = mySlotId === slot.id;
                  const eligible = isSlotEligible(slot);
                  const isPod = et === "17x17-pod";
                  const hasFacing = slot.facing && !isRes;
                  const isClickable = !isRes && (eligible || iAmHere);

                  return (
                    <div
                      key={slot.id}
                      ref={(el) => (slotRefs.current[slot.id] = el)}
                      className={[
                        "s4-slot",
                        !isClickable ? "s4-slot--disabled" : "",
                        iAmHere ? "s4-slot--mine" : "",
                        isPod ? "s4-slot--pod" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        gridColumn: slot.gridColumn,
                        gridRow: slot.gridRow,
                        backgroundColor: iAmHere ? "#00c853" : TENT_COLORS[et] || "#d9d9d9",
                        opacity: !eligible && !isRes && !iAmHere ? 0.35 : 1,
                        cursor: isClickable ? "pointer" : "not-allowed",
                        position: "relative",
                        ...(hasFacing ? getFacingBorders(slot.facing) : {}),
                      }}
                      onClick={() => {
                        if (isClickable) {
                          setMessage("");
                          setSelectedSlot(slot.id);
                          setSelectedSub(null);
                        }
                      }}
                    >
                      {used > 0 && !isRes && <div className="s4-slot-overlay" style={{ opacity: (used / cap) * 0.35 }} />}
                      <strong className="s4-slot-label">{slot.label}</strong>

                      <TileSubPreview slotId={slot.id} subBookings={subBookings} effectiveType={et} facing={slot.facing} />

                      {!isPod && used === 1 && !isRes && cap === 1 && (
                        <span className="s4-slot-initial">
                          {Object.values(tileSubs)[0]?.vendorName?.[0]?.toUpperCase()}
                        </span>
                      )}
                      {cap > 1 && !isRes && <small className="s4-slot-count">{used}/{cap}</small>}
                      {iAmHere && <span className="s4-slot-mine">✓</span>}
                      {hasFacing && <FacingArrow facing={slot.facing} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spot Detail & Selection Modal */}
      {selectedSlot && activeSlotConfig && (
        <div className="s4-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setSelectedSlot(null); }}>
          <div className="s4-modal">
            <div className="s4-modal-header">
              <h3 className="s4-modal-title">{activeSlotConfig.label}</h3>
              <button className="s4-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
            </div>

            <div className="s4-modal-meta">
              {activeIsReserved ? (
                <span className="s4-badge s4-badge--reserved">Reserved by Admin</span>
              ) : (
                <span
                  className="s4-badge"
                  style={{
                    background: (TENT_COLORS[activeEffType] || "#999") + "22",
                    color: TENT_COLORS[activeEffType] || "#999",
                  }}
                >
                  {TENT_LABELS[activeEffType] || activeEffType}
                </span>
              )}
              {activeCapacity > 1 && (
                <span className="s4-badge s4-badge--gray">
                  {activeFilledCount}/{activeCapacity} filled
                </span>
              )}
              {activeSlotConfig.facing && (
                <span className="s4-badge s4-badge--gray">{FACING_LABEL[activeSlotConfig.facing]}</span>
              )}
            </div>

            {!activeIsReserved && activeDef && (
              <div className="s4-floorplan">
                <div className="s4-floorplan-label">
                  {activeIsMultiSub ? "Floor plan — pick a side to claim" : "Slot"}
                </div>
                <div
                  className={`s4-floorplan-grid s4-floorplan-grid--${activeDef.layout}`}
                  style={activeOrientedStyle}
                >
                  {activeDef.subs.map((sub) => {
                    const booking = activeTileSubs[sub];
                    const isMe = booking?.vendorId === application?.id;
                    const isSelected = selectedSub === sub;
                    const subLabel = activeIsMultiSub ? `${activeSlotConfig.label}${sub}` : activeSlotConfig.label;
                    const canPick = !booking && (!mySlotId || mySlotId === selectedSlot);

                    return (
                      <div
                        key={sub}
                        className={[
                          "s4-sub-slot",
                          booking ? "s4-sub-slot--filled" : "s4-sub-slot--empty",
                          isSelected ? "s4-sub-slot--selected" : "",
                          isMe ? "s4-sub-slot--mine" : "",
                          canPick ? "s4-sub-slot--pickable" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{ background: isMe ? "#00c853" : TENT_COLORS[activeEffType] || "#ccc" }}
                        onClick={() => {
                          if (canPick) setSelectedSub((prev) => (prev === sub ? null : sub));
                        }}
                      >
                        <span className="s4-sub-slot-id">{subLabel}</span>
                        {booking ? (
                          <>
                            <div className="s4-sub-slot-avatar">
                              {booking.vendorName?.[0]?.toUpperCase() || "?"}
                            </div>
                            <span className="s4-sub-slot-name">{isMe ? "You" : booking.vendorName}</span>
                            <span
                              className={`s4-sub-slot-pill ${
                                booking.selfSelected ? "s4-sub-slot-pill--self" : "s4-sub-slot-pill--admin"
                              }`}
                            >
                              {booking.selfSelected ? <><CrownIcon /> Self</> : <><PencilIcon /> Admin</>}
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="s4-sub-slot-plus">{canPick ? "+" : "·"}</div>
                            <span className="s4-sub-slot-empty-lbl">
                              {canPick ? (isSelected ? "Selected" : "tap to select") : "unavailable"}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {message && <div className={`s4-message s4-message--${messageType}`}>{message}</div>}

            {!activeIsReserved && (
              <div className="s4-modal-actions">
                {activeIAmHere && (
                  <button className="s4-btn s4-btn--danger" onClick={releaseSlot} disabled={loading}>
                    {loading ? "Releasing…" : "Release Spot"}
                  </button>
                )}
                {!activeIAmHere && activeEligible && selectedSub && !activeTileSubs[selectedSub] && (
                  <button
                    className="s4-btn s4-btn--primary"
                    onClick={() => claimSub(selectedSlot, selectedSub, claimTentMode)}
                    disabled={loading}
                  >
                    {loading
                      ? "Claiming…"
                      : `Claim ${activeIsMultiSub ? `${activeSlotConfig.label}${selectedSub}` : activeSlotConfig.label} (+KES 7,000)`}
                  </button>
                )}
                {!activeIAmHere && activeEligible && !selectedSub && activeFilledCount < activeCapacity && (
                  <p className="s4-modal-notice">Pick a side above to claim it.</p>
                )}
                {!activeIAmHere && activeFilledCount >= activeCapacity && (
                  <p className="s4-modal-notice">This slot is full.</p>
                )}
              </div>
            )}

            <button className="s4-btn s4-btn--ghost" onClick={() => setSelectedSlot(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}